import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, ExternalLink, ThumbsUp, ThumbsDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewDetail = () => {
  const { slug } = useParams();
  const [review, setReview] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    content: ''
  });
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    fetchReview();
    fetchComments();
  }, [slug]);

  const fetchReview = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reviews/${slug}`);
      setReview(data);
    } catch (error) {
      console.error('Error fetching review:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reviews/${slug}/comments`);
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/reviews/${slug}/comments`, commentForm);
      setSubmitMessage('Comment submitted for moderation!');
      setCommentForm({ author_name: '', author_email: '', content: '' });
      setTimeout(() => setSubmitMessage(''), 3000);
    } catch (error) {
      setSubmitMessage('Error submitting comment');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={24}
            className={star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div data-testid="loading-state" className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!review) {
    return (
      <div data-testid="not-found" className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-bold mb-4">Review Not Found</h2>
          <Link to="/" className="text-primary hover:underline">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="review-detail-page" className="min-h-screen bg-background">
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-sm text-primary hover:underline mb-4 inline-block">
            ← Back to Reviews
          </Link>
          <div className="mb-4">
            <span className="text-sm font-medium text-primary uppercase tracking-wide">
              {review.category?.name}
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-6">
            {review.title}
          </h1>
          <div className="flex items-center space-x-6 text-sm text-muted-foreground">
            <span>By {review.author}</span>
            <span>•</span>
            <span>{new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
        </div>

        {/* Featured Image */}
        <div className="mb-12">
          <img
            src={review.featured_image}
            alt={review.title}
            className="w-full h-96 object-cover border border-border"
          />
        </div>

        {/* Rating */}
        <div className="bg-muted p-8 mb-12">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-serif text-2xl font-bold mb-4">Our Rating</h3>
              <div className="flex items-center space-x-4">
                {renderStars(review.rating)}
                <span className="text-3xl font-bold">{review.rating}/5</span>
              </div>
            </div>
            {review.affiliate_link && (
              <a
                href={review.affiliate_link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buy-now-btn"
                className="px-8 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:opacity-90 smooth-transition flex items-center space-x-2"
              >
                <span>Buy Now</span>
                <ExternalLink size={18} />
              </a>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="prose-custom mb-12">
          <p className="text-lg leading-relaxed text-foreground">{review.description}</p>
        </div>

        {/* Pros & Cons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="border border-border p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-100 rounded-sm">
                <ThumbsUp size={24} className="text-green-600" />
              </div>
              <h3 className="font-serif text-2xl font-bold">Pros</h3>
            </div>
            <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
              {review.pros}
            </div>
          </div>
          <div className="border border-border p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-red-100 rounded-sm">
                <ThumbsDown size={24} className="text-red-600" />
              </div>
              <h3 className="font-serif text-2xl font-bold">Cons</h3>
            </div>
            <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
              {review.cons}
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="border border-border p-8 mb-12">
          <h3 className="font-serif text-2xl font-bold mb-6">Specifications</h3>
          <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
            {review.specs}
          </div>
        </div>

        {/* Tags */}
        {review.tags && review.tags.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center space-x-3 flex-wrap">
              <span className="text-sm font-medium">Tags:</span>
              {review.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="px-3 py-1 bg-muted text-sm rounded-sm"
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Comments Section */}
      <div className="border-t border-border bg-muted">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h3 data-testid="comments-section" className="font-serif text-3xl font-bold mb-8">
            Reader Comments
          </h3>

          {/* Comment Form */}
          <div className="bg-card border border-border p-8 mb-12">
            <h4 className="font-semibold text-lg mb-6">Leave a Comment</h4>
            <form onSubmit={handleCommentSubmit} data-testid="comment-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Your Name"
                  data-testid="comment-name-input"
                  value={commentForm.author_name}
                  onChange={(e) => setCommentForm({ ...commentForm, author_name: e.target.value })}
                  className="px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  data-testid="comment-email-input"
                  value={commentForm.author_email}
                  onChange={(e) => setCommentForm({ ...commentForm, author_email: e.target.value })}
                  className="px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <textarea
                placeholder="Your Comment"
                data-testid="comment-content-input"
                value={commentForm.content}
                onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
                className="w-full px-4 py-3 border border-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                rows="4"
                required
              />
              <button
                type="submit"
                data-testid="comment-submit-btn"
                className="px-6 py-3 bg-primary text-primary-foreground font-medium rounded-sm hover:opacity-90 smooth-transition"
              >
                Submit Comment
              </button>
              {submitMessage && (
                <p data-testid="comment-submit-message" className="mt-4 text-sm text-green-600">
                  {submitMessage}
                </p>
              )}
            </form>
          </div>

          {/* Comments List */}
          <div className="space-y-6">
            {comments.length === 0 ? (
              <p data-testid="no-comments" className="text-muted-foreground">No comments yet. Be the first to comment!</p>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} data-testid={`comment-${comment.id}`} className="bg-card border border-border p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-semibold">
                      {comment.author_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold">{comment.author_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{comment.content}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetail;